<template>
  <el-form>
    <div class="top_right">
      <el-form-item v-if="showRight">
        <div class="title">
          <span>{{ $t('withdraw.otherMethod.withdrawalMethod') }}</span>
        </div>
      </el-form-item>
      <!--credit Card Method-->
      <el-form-item v-if="topForm.showCC">
        <SelectWithdraw
          name="creditCard"
          key="creditCard"
          :value="null"
          :showCreditCard="true"
          :amount="creditCardAmount"
          :reset="reset"
          :disabled="true"
        ></SelectWithdraw>
      </el-form-item>

      <!--secondary Method options-->
      <el-form-item v-if="topForm.showLBT">
        <SelectWithdraw
          v-model="secondWithdrawalType"
          :value="secondWithdrawalType"
          name="secondWithdrawalType"
          key="secondWithdrawalType"
          :availableTypes="availableTypesOfSecondWithdrawal"
          :amount="secondWithdrawAmount"
          :reset="reset"
          :disabled="disableSecondMethod"
          :selectText="getSelectText('secondWithdraw')"
          :cpsPayments="cpsPayments"
          @setWithdraw="setSecondWithdraw"
        ></SelectWithdraw>
        <FeeTip
          :withdrawalType="secondWithdrawalType"
          :currency="withdrawForm.currency"
          :withdrawalAmount="secondWithdrawAmount - socialTradingPerformanceFee"
          :mt4Account="withdrawForm.account"
        />
      </el-form-item>

      <!--Other Method options-->
      <el-form-item v-if="topForm.showOtherPayment">
        <SelectWithdraw
          ref="otherMethodSelect"
          v-model="withdrawalType"
          :value="withdrawalType"
          name="withdrawalType"
          key="withdrawalType"
          :availableTypes="availableTypes"
          :paymentAuthConfig="paymentAuthConfig"
          :amount="withdrawAmount"
          :reset="reset"
          :disabled="disableOtherMeThod"
          :selectText="getSelectText('withdraw')"
          :cpsPayments="cpsPayments"
          @setWithdraw="setWithdraw"
        ></SelectWithdraw>
        <FeeTip
          :withdrawalType="withdrawalType"
          :currency="withdrawForm.currency"
          :withdrawalAmount="withdrawAmount - socialTradingPerformanceFee"
          :mt4Account="withdrawForm.account"
        />
      </el-form-item>

      <!-- withdraw Note -->
      <el-form-item v-if="topForm.showCC && (topForm.showOtherPayment || topForm.showLBT)">
        <div class="withdraw_note">{{ $t('withdraw.default.withdrawNote') }}</div>
      </el-form-item>
    </div>
  </el-form>
</template>

<script>
import SelectWithdraw from './SelectWithdraw';
import { apiGetNonCreditCardWithdrawTypeCP } from '@/resource';
import { apiGetCpsWithdrawalChannel } from '@/resource/cps';
import { COUNTRY_IBT } from '@/constants/internationalBankTransfer';
import rounding from '@/util/rounding';
import { countryIsoCode } from '@/constants/countryCode';
import {
  CURRENCY_RESTRICTED_CHANNELS,
  MULTIPLE_CURRENCY_CHANNELS,
  CURRENCY_AVAILABLE_TYPES
} from '@/constants/withdrawChannelCode';
import { getIbtRedirectPath } from '@/util/register/getIbtRedirectPath';
import { withdrawListingIndex } from '@/constants/withdrawListingIndex';
import { countryCodeEnum } from '@/constants/country';
import FeeTip from '@/components/withdraw/FeeTip';

export default {
  name: 'TopRight',
  components: { SelectWithdraw, FeeTip },
  props: {
    withdrawForm: Object,
    paymentAuthConfig: Object,
    topForm: Object,
    showRight: Boolean,
    reset: Boolean,
    socialTradingPerformanceFee: Number,
    disableOtherMeThod: {
      type: Boolean,
      default: false
    },
    disableSecondMethod: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      secondWithdrawalType: null,
      withdrawalType: null,
      secondAvailableTypes: [],
      availableTypes: [],
      withdrawalTypes: [],
      withdrawData: {},
      supportCurrency: ['USD', 'USC'],
      cpsPayments: [],
      cryptoOnlyCountry: [countryCodeEnum.LEBANON, countryCodeEnum.SYRIA, countryCodeEnum.YEMEN]
    };
  },
  computed: {
    creditCardAmount() {
      return this.topForm.totalCcBalance >= this.withdrawData.amount
        ? this.withdrawData.amount
        : this.topForm.totalCcBalance;
    },
    secondWithdrawAmount() {
      let amt = parseFloat(rounding(Math.round, this.withdrawData.amount - this.topForm.totalCcBalance, 2));
      return this.topForm.lbtBalance > amt ? amt : this.topForm.lbtBalance;
    },
    withdrawAmount() {
      if (this.secondWithdrawAmount === this.topForm.lbtBalance)
        return parseFloat(
          rounding(Math.round, this.withdrawData.amount - this.topForm.totalCcBalance - this.topForm.lbtBalance, 2)
        );
      else return 0;
    },
    countryCode() {
      return parseInt(this.$store.state.common.countryCode);
    },
    getCountryIsoCode() {
      return countryIsoCode.find(c => c.id === parseInt(this.$store.state.common.countryCode)).code;
    },
    availableTypesOfSecondWithdrawal() {
      return this.availableTypes.filter(type => this.topForm.lbtTypesAndBalancesData.lbtType.includes(type.id));
    }
  },
  watch: {
    topForm: {
      handler() {
        if (!this.reset) {
          this.fetchWithdrawalMethod();
          this.withdrawData = { ...this.withdrawForm };
        }
      },
      deep: true
    }
  },
  methods: {
    fetchWithdrawalMethod() {
      apiGetNonCreditCardWithdrawTypeCP({ accountNumber: this.withdrawForm.account.toString() })
        .then(resp => {
          if (resp.status === 200) {
            // Filter unsupported USD, USC currencies' types
            this.availableTypes = this.supportCurrency.includes(this.withdrawData.currency)
              ? resp.data
              : resp.data.filter(type => ![4, 49, 65, 66].includes(type.id));

            // Filter IBT (2) for unsupported IBT countries
            if (!COUNTRY_IBT.includes(this.countryCode)) {
              this.availableTypes = this.availableTypes.filter(type => ![2].includes(type.id));
            }

            //Filter to ONLY USDT for Lebanon, Syria and Yemen
            const notUSDT = [4, 34, 80, 81];

            if (this.cryptoOnlyCountry.includes(this.countryCode)) {
              this.availableTypes = this.availableTypes.filter(type => !notUSDT.includes(type.id));
            }

            this.availableTypes = this.availableTypes.filter(value =>
              value.availableCurrencies == null || value.availableCurrencies.length < 1
                ? true
                : value.availableCurrencies.includes(this.withdrawForm.currency)
            );
            // get cps withdrawal type list
            this.fetchCpsWithdrawalMethod();
          } else {
            this.$message({ message: this.$t('withdraw.default.fetchTradeAccFailed'), type: 'error' });
          }
        })
        .catch(err => {
          this.$message({ message: this.$t('withdraw.default.fetchTradeAccFailed'), type: 'error' });
        });
    },
    async fetchCpsWithdrawalMethod() {
      await apiGetCpsWithdrawalChannel({ country: this.getCountryIsoCode })
        .then(resp => {
          if (resp.status === 200) {
            // combine channels into withdrawal type list
            resp.data.data.withdrawalType.channels.forEach(m => {
              let merchantVariable = JSON.parse(m.merchant_variable);

              let withdrawalList = {
                amountLimit: merchantVariable.amountLimit,
                availableCurrencies: null,
                category: 0,
                id: m.withdrawTypeId,
                cps: m,
                nameKey: merchantVariable.description_title
              };

              // restrict payment channel currencies
              this.restrictPaymentChannelCurrencies(withdrawalList);
              this.cpsPayments.push(m.withdrawTypeId);
            });

            // remove duplicates
            this.availableTypes = [...new Map(this.availableTypes.map(m => [m.id, m])).values()];
            this.cpsPayments = [...new Set(this.cpsPayments)];

            // sort payment channel order
            this.availableTypes = this.sortPaymentChannel(this.availableTypes);
          } else {
            this.$message({ message: this.$t('withdraw.default.fetchTradeAccFailed'), type: 'error' });
          }
        })
        .catch(err => {
          this.$message({ message: this.$t('withdraw.default.fetchTradeAccFailed'), type: 'error' });
        });

      this.reorderVnPaymentChannel();
      this.reorderChAreaSequence();
    },
    restrictPaymentChannelCurrencies(payment) {
      // check if payment channel is under restricted list
      if (CURRENCY_RESTRICTED_CHANNELS.includes(payment.id)) {
        if (
          CURRENCY_AVAILABLE_TYPES.hasOwnProperty(this.withdrawData.currency) &&
          CURRENCY_AVAILABLE_TYPES[this.withdrawData.currency].includes(payment.id)
        ) {
          if (MULTIPLE_CURRENCY_CHANNELS.includes(payment.id)) {
            if (payment.cps.currency_code == this.withdrawData.currency) {
              this.availableTypes.push(payment);
            }
            if (payment.cps.currency_code === 'USD' && this.withdrawForm.currency === 'USC') {
              this.availableTypes.push(payment);
            }
          } else {
            this.availableTypes.push(payment);
          }
        }
      } else {
        this.availableTypes.push(payment);
      }
    },
    reorderVnPaymentChannel() {
      let vietBank = [];

      this.availableTypes.forEach((m, i) => {
        if (m.id === 8 && this.getCountryIsoCode === 'VN') {
          vietBank = this.availableTypes.splice(i, 1);
        }
      });

      if (vietBank.length > 0) {
        this.availableTypes.unshift(vietBank[0]);
      }
    },
    reorderChAreaSequence() {
      let isChArea = false;

      if (
        this.getCountryIsoCode === 'CN' ||
        this.getCountryIsoCode === 'HK' ||
        this.getCountryIsoCode === 'MO' ||
        this.getCountryIsoCode === 'TW'
      ) {
        isChArea = true;
      }

      if (isChArea === true) {
        let unionPay = [];
        let usdtChannel = [];
        let bankTransferChannel = [];

        this.availableTypes.forEach((m, i) => {
          if (m.id === 4) {
            unionPay = this.availableTypes.splice(i, 1);
          } else if (m.id === 36) {
            usdtChannel = this.availableTypes.splice(i, 1);
          } else if (m.id === 2) {
            bankTransferChannel = this.availableTypes.splice(i, 1);
          }
        });

        if (bankTransferChannel.length > 0) {
          this.availableTypes.unshift(bankTransferChannel[0]);
        }
        if (usdtChannel.length > 0) {
          this.availableTypes.unshift(usdtChannel[0]);
        }
        if (unionPay.length > 0) {
          this.availableTypes.unshift(unionPay[0]);
        }
      }
    },
    sortPaymentChannel(withdrawTypes) {
      let withdrawListOrder = [];
      let sortedWithdrawList = [];

      withdrawListingIndex.forEach(wi => {
        if (wi.id == this.countryCode) {
          withdrawListOrder = wi.order;
        }
      });

      if (withdrawListOrder.length == 0) {
        withdrawListingIndex.forEach(wi => {
          if (wi.id == 'global') {
            withdrawListOrder = wi.order;
          }
        });
      }

      withdrawListOrder.forEach(wi => {
        withdrawTypes.forEach(withdrawType => {
          if (wi == withdrawType.id) {
            sortedWithdrawList.push(withdrawType);
          }
        });
      });

      const remainingPayments = withdrawTypes.filter(v => !withdrawListOrder.includes(v.id));
      sortedWithdrawList = sortedWithdrawList.concat(remainingPayments);

      // IBT channel need to put in last order
      const ibtChannelIndex = sortedWithdrawList.findIndex(sw => sw.id === 2);
      const [ibtChannel] = sortedWithdrawList.splice(ibtChannelIndex, 1);
      sortedWithdrawList.push(ibtChannel);
      // move FX-IR channel to first (Iran)
      if (this.countryCode === countryCodeEnum.IRAN) {
        const fxirChannelIndex = sortedWithdrawList.findIndex(channel => channel.id === 97);
        if (fxirChannelIndex !== -1) {
          const fxirChannel = JSON.parse(JSON.stringify(sortedWithdrawList[fxirChannelIndex]));
          sortedWithdrawList.splice(fxirChannelIndex, 1);
          sortedWithdrawList.unshift(fxirChannel);
        }
      }

      return sortedWithdrawList;
    },
    cpsSelect(payment) {
      this.$store.dispatch('cps/actionSetWithdrawalPayment', payment);
    },
    secondaryCpsSelect(payment) {
      this.$store.dispatch('cps/actionSetSecondaryWithdrawalPayment', payment);
    },
    async setWithdraw(val) {
      this.withdrawalType = val.withdrawalType;
      // withdrawalType === 2 === IBT transfer
      if (val.withdrawalType === 2) {
        const ibtRedirectPath = await getIbtRedirectPath();
        if (ibtRedirectPath?.length > 0) {
          this.$router.push({ path: ibtRedirectPath, query: { type: 'withdraw' } });
        }
      }

      if (val.withdrawalType && this.cpsPayments.includes(val.withdrawalType)) {
        let selectedCps = this.availableTypes.find(w => w.id == val.withdrawalType);
        this.cpsSelect(selectedCps.cps);
      }

      this.$emit('setWithdraw', val);
    },
    async setSecondWithdraw(val) {
      this.secondWithdrawalType = val.withdrawalType;
      if (val.withdrawalType && this.cpsPayments.includes(val.withdrawalType)) {
        let selectedCps = this.availableTypes.find(w => w.id == val.withdrawalType);
        this.secondaryCpsSelect(selectedCps.cps);
      }

      this.$emit('setSecondWithdraw', val);
    },
    getSelectText(parameter) {
      switch (parameter) {
        case 'secondWithdraw':
          return 'Local Bank Transfer';
        case 'withdraw':
          return this.$t('withdraw.default.addWithdrawalMethod');
        default:
          break;
      }
    },
    cancelChanges() {
      this.$refs.otherMethodSelect.cancelChanges();
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/css/components/withdraw/topRight.scss';
</style>
